import { useParams } from 'react-router-dom'
import { isEqual, isNumber } from 'lodash'
import {
    useFetchAnswerCategoryMismatchPredictionsQuery,
    useFetchQuestionAnswersQuery,
    useFetchTextAssignmentSubmissionsQuery,
} from '../../../../../store/apis/submission.api'
import { getReviewStatusName, SubmissionReviewStatuses } from '../../../../../constants/submission-review-statuses'
import useQueryParam from '../../../../../hooks/useQueryParam'
import {
    ASSIGNEE_IDS_PARAM,
    CONFIDENCE_LEVELS_PARAM,
    FLAGGED_FOR_MYSELF_PARAM,
    FLAGGED_PARAM,
    GROUP_BY_PARAM,
    GROUPS_SORT_PARAM,
    MISMATCH_PARAM,
    REVIEW_STATUS_PARAM,
    SUBMISSION_SEARCH_PARAM,
    TEST_SET_ONLY_PARAM,
} from '../../../../../constants/query-params'
import {
    DEFAULT_SUBMISSION_GROUP_BY_TYPE_FOR_ALL_QUESTIONS_PAGE,
    DEFAULT_SUBMISSION_GROUP_BY_TYPE_FOR_QUESTION_PAGE,
    SubmissionGroupByTypes,
} from '../../../../../constants/groups-by-types'
import { useCallback, useDeferredValue, useEffect, useMemo } from 'react'
import {
    DEFAULT_TEXT_SUBMISSION_GROUPS_SORTING_TYPE,
    TextSubmissionGroupsSortingTypes,
} from '../../../../../constants/sorting-types'
import { useDispatch, useSelector } from 'react-redux'
import assignmentSelectors from '../../../../../store/selectors/assignment.selectors'
import { isMba, isNewTextAssignment } from '../../../../../utils/assignment.helper'
import { useVersionId } from '../../../../../utils/parseURL.helper'
import groupSelectors from '../../../../../store/selectors/group.selectors'
import { groupActions } from '../../../../../store/slices/group.slice'
import useDefaultGroupsCollapse from '../../../../../hooks/useDefaultGroupsCollapse'
import { DEFAULT_COLLAPSE_THRESHOLD } from '../../../../../constants/collapse-thresholds'
import useRubricContext from '../../../../../hooks/useRubricContext'

const useControlGroupsCollapse = groups => {
    const isGloballyCollapsed = useSelector(groupSelectors.isGloballyCollapsed)
    const collapsedGroupIds = useSelector(groupSelectors.collapsedGroupIds)
    const dispatch = useDispatch()

    useEffect(() => {
        if (!isGloballyCollapsed) return
        
        const groupIds = groups.map(group => group.id)
        // Only update if the collapsed group IDs don't match the current groups
        const idsMatch = groupIds.length === collapsedGroupIds.length && 
                         groupIds.every(id => collapsedGroupIds.includes(id))
        
        if (!idsMatch) {
            dispatch(groupActions.setCollapsedGroupsKeys(groupIds))
        }
    }, [groups, dispatch, isGloballyCollapsed, collapsedGroupIds])
}

const useSubmissionGroups = ({ applyFilter = true, groupBy, questionRubricId: questionRubricIdProp, skip = false } = {}) => {
    const assignmentType = useSelector(assignmentSelectors.type)
    const language = useSelector(assignmentSelectors.language)
    const { token, layerId } = useParams()
    const groupByFromUrl = useQueryParam(GROUP_BY_PARAM)
    const groupByParam =
        groupBy || 
        groupByFromUrl ||
        (layerId === 'all' || layerId === 'dashboard'
            ? DEFAULT_SUBMISSION_GROUP_BY_TYPE_FOR_ALL_QUESTIONS_PAGE
            : DEFAULT_SUBMISSION_GROUP_BY_TYPE_FOR_QUESTION_PAGE)
    const searchParam = useQueryParam(SUBMISSION_SEARCH_PARAM)
    const sortParam = useQueryParam(GROUPS_SORT_PARAM) || DEFAULT_TEXT_SUBMISSION_GROUPS_SORTING_TYPE
    const onlyEscalatedParam = useQueryParam(FLAGGED_PARAM) === 'true'
    const onlyEscalatedForMyselfParam = useQueryParam(FLAGGED_FOR_MYSELF_PARAM) === 'true'
    const mismatchParam = Boolean(useQueryParam(MISMATCH_PARAM))
    const reviewStatusParam = useQueryParam(REVIEW_STATUS_PARAM)
    const confidenceLevelsParam = useQueryParam(CONFIDENCE_LEVELS_PARAM)
    const assigneeIdsParam = useQueryParam(ASSIGNEE_IDS_PARAM)
    const testSetOnlyParam = useQueryParam(TEST_SET_ONLY_PARAM) === 'true'

    const versionId = useVersionId()
    const isTextAssignment = isNewTextAssignment(language)

    const { 
        questionRubricId: contextQuestionRubricId, 
        categories, 
        isFetching: isFetchingContext 
    } = useRubricContext({ skip })
    
    const questionRubricId = questionRubricIdProp || contextQuestionRubricId

    const { data: submissions, isFetching: isFetchingSubmissions } = useFetchTextAssignmentSubmissionsQuery(
        {
            token,
            versionId,
            testSetOnly: testSetOnlyParam,
            onlyEscalated: onlyEscalatedParam,
            onlySelfEscalated: onlyEscalatedForMyselfParam,
            search: applyFilter ? searchParam : undefined,
            assigneeIds: applyFilter ? assigneeIdsParam?.split(',')?.map(Number) : undefined,
        },
        { skip: skip || isNumber(questionRubricId) || !isTextAssignment },
    )
    const { data: questionRubricSubmissions, isFetching: isFetchingQuestionRubricSubmissions } = useFetchQuestionAnswersQuery(
        {
            id: questionRubricId,
            search: applyFilter ? searchParam : undefined,
            versionId,
            assigneeIds: applyFilter ? assigneeIdsParam?.split(',')?.map(Number) : undefined,
            testSetOnly: testSetOnlyParam,
            onlyEscalated: onlyEscalatedParam,
            onlySelfEscalated: onlyEscalatedForMyselfParam,
        },
        { skip: skip || !isNumber(questionRubricId) || !isTextAssignment },
    )

    const { data: answerCategoryMismatchPredictions } = useFetchAnswerCategoryMismatchPredictionsQuery(
        { questionRubricId },
        {
            skip: skip || !isNumber(questionRubricId) || isMba(assignmentType) || !isTextAssignment,
        },
    )

    const filters = useMemo(
        () => ({
            searchParam,
            mismatchParam,
            reviewStatusParam,
            confidenceLevelsParam,
            questionId: questionRubricId,
        }),
        [searchParam, mismatchParam, reviewStatusParam, confidenceLevelsParam, questionRubricId],
    )
    const deferredFilters = useDeferredValue(filters)

    const filterSubmissions = useCallback(
        (
            submissions,
            {
                searchParam,
                mismatchParam,
                reviewStatusParam,
                confidenceLevelsParam,
                questionId,
            },
        ) => {
            if (!applyFilter) return submissions

            const lowerCaseSearchParam = searchParam?.toLowerCase() || ''

            const matchesSearchParam = submission => {
                if (!searchParam || isNumber(questionId)) return true
                return submission.studentId?.toLowerCase().includes(lowerCaseSearchParam)
            }

            const matchesMismatchParam = submission => {
                if (!mismatchParam) return true
                return submission.mismatchPredicted === mismatchParam
            }

            const matchesReviewStatusParam = submission => {
                if (['any', null].includes(reviewStatusParam)) return true
                return submission.reviewStatus === Number(reviewStatusParam)
            }

            const matchesConfidenceLevelsParam = submission => {
                if (!confidenceLevelsParam) return true
                const confidenceLevels = confidenceLevelsParam.split(',').map(Number)
                return confidenceLevels.includes(submission.confidence)
            }

            return submissions.filter(
                submission =>
                    matchesSearchParam(submission) &&
                    matchesMismatchParam(submission) &&
                    matchesReviewStatusParam(submission) &&
                    matchesConfidenceLevelsParam(submission),
            )
        },
        [applyFilter],
    )

    const addMismatchPredictedPropertyToSubmissions = useCallback((submissions, answerCategoryMismatchPredictions) => {
        return submissions
            ?.map(submission => ({
                ...submission,
                mismatchPredicted: answerCategoryMismatchPredictions?.some(
                    mismatchPrediction => mismatchPrediction.answerId === submission.answerId,
                ),
            }))
            ?.sort((a, b) => a.mismatchPredicted - b.mismatchPredicted)
    }, [])

    const sortSubmissionsInGroups = useCallback(
        groups =>
            groups.map(group => ({
                ...group,
                submissions: group.submissions.sort((a, b) => b.confidence - a.confidence),
            })),
        [],
    )

    const createGroupsForQuestion = useCallback(
        (questionSubmissions, categories, filters) => {
            let resultSubmissions = questionSubmissions || []
            resultSubmissions = addMismatchPredictedPropertyToSubmissions(
                resultSubmissions,
                answerCategoryMismatchPredictions,
            )
            resultSubmissions = filterSubmissions(resultSubmissions, filters)
            let groups = []
            if (groupByParam === SubmissionGroupByTypes.REVIEW_STATUS) {
                groups = [
                    {
                        id: SubmissionReviewStatuses.UNREVIEWED,
                        title: getReviewStatusName(SubmissionReviewStatuses.UNREVIEWED),
                        submissions: resultSubmissions?.filter(submission => !submission.approveStatus),
                    },
                    {
                        id: SubmissionReviewStatuses.REVIEWED,
                        title: getReviewStatusName(SubmissionReviewStatuses.REVIEWED),
                        submissions: resultSubmissions?.filter(submission => submission.approveStatus),
                    },
                ]
            } else if (groupByParam === SubmissionGroupByTypes.CATEGORY) {
                const categoryGroups =
                    categories?.map(category => ({
                        id: category.id,
                        title: category.name,
                        colorId: category.colorId,
                        submissions: resultSubmissions?.filter(submission => submission.categoryId === category.id),
                    })) || []

                const nullCategoryGroup = {
                    id: 'no-category',
                    title: 'No Category',
                    submissions: resultSubmissions?.filter(submission => submission.categoryId === null),
                }

                groups = [...categoryGroups, nullCategoryGroup]
            }
            return sortSubmissionsInGroups(groups)
        },
        [
            addMismatchPredictedPropertyToSubmissions,
            answerCategoryMismatchPredictions,
            filterSubmissions,
            groupByParam,
            sortSubmissionsInGroups,
        ],
    )

    const createGroupsForSubmissions = useCallback(
        (submissions, filters) => {
            let resultSubmissions = submissions || []
            resultSubmissions = filterSubmissions(resultSubmissions, filters)
            if (groupByParam === SubmissionGroupByTypes.REVIEW_STATUS) {
                return Object.values(SubmissionReviewStatuses)?.map(reviewStatus => ({
                    id: reviewStatus,
                    title: getReviewStatusName(reviewStatus),
                    submissions: resultSubmissions?.filter(submission => submission.reviewStatus === reviewStatus),
                    reviewStatus,
                }))
            }
            return []
        },
        [filterSubmissions, groupByParam],
    )

    const sortGroups = useCallback((groups, sortParam) => {
        if (groups.length === 0) return groups
        switch (sortParam) {
            case TextSubmissionGroupsSortingTypes.A_Z.value:
                return groups.sort((a, b) => a.title.localeCompare(b.title))
            case TextSubmissionGroupsSortingTypes.Z_A.value:
                return groups.sort((a, b) => b.title.localeCompare(a.title))
            case TextSubmissionGroupsSortingTypes.SMALL_TO_LARGE.value:
                return groups.sort((a, b) => a.submissions?.length - b.submissions?.length)
            case TextSubmissionGroupsSortingTypes.LARGE_TO_SMALL.value:
                return groups.sort((a, b) => b.submissions?.length - a.submissions?.length)
            default:
                return groups
        }
    }, [])

    const groups = useMemo(() => {
        let resultGroups
        if (isNumber(questionRubricId)) {
            resultGroups = createGroupsForQuestion(questionRubricSubmissions, categories, deferredFilters)
        } else {
            resultGroups = createGroupsForSubmissions(submissions, deferredFilters)
        }
        return sortGroups(resultGroups, sortParam)
    }, [
        questionRubricId,
        questionRubricSubmissions,
        categories,
        submissions,
        createGroupsForQuestion,
        createGroupsForSubmissions,
        sortParam,
        sortGroups,
        deferredFilters,
    ])

    // Create flat list of filtered submissions
    const flatSubmissions = useMemo(() => {
        if (!isTextAssignment) return []
        
        let resultSubmissions
        if (isNumber(questionRubricId)) {
            // For specific question, get submissions from questionSubmissions
            resultSubmissions = questionRubricSubmissions || []
            resultSubmissions = addMismatchPredictedPropertyToSubmissions(
                resultSubmissions,
                answerCategoryMismatchPredictions,
            )
        } else {
            // For all submissions, get from submissions
            resultSubmissions = submissions || []
        }
        
        // Apply the same filtering logic as used for groups
        return filterSubmissions(resultSubmissions, deferredFilters)
    }, [
        isTextAssignment,
        questionRubricId,
        questionRubricSubmissions,
        submissions,
        addMismatchPredictedPropertyToSubmissions,
        answerCategoryMismatchPredictions,
        filterSubmissions,
        deferredFilters,
    ])

    // Apply default collapse on first load (>= threshold submissions)
    useDefaultGroupsCollapse(groups, flatSubmissions.length, DEFAULT_COLLAPSE_THRESHOLD)
    useControlGroupsCollapse(groups)
    
    return {
        groups: isTextAssignment ? groups : [],
        submissions: flatSubmissions,
        isFetching: isTextAssignment ? (isFetchingSubmissions || isFetchingQuestionRubricSubmissions || isFetchingContext) : false,
        totalSubmissions: isTextAssignment ? (submissions?.length || questionRubricSubmissions?.length) : 0,
        isPendingTransition: isTextAssignment ? !isEqual(filters, deferredFilters) : false,
        isAnySubmissionReviewed: isTextAssignment ? submissions?.some(submission => submission.reviewStatus === SubmissionReviewStatuses.REVIEWED) : false,
    }
}

export default useSubmissionGroups
